import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Spinner, Alert, Button, Card } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { announcementService } from '../services/api';
import config from '../config';
import './AnnouncementView.css';

const AnnouncementView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [announcement, setAnnouncement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAnnouncement();
  }, [id]);

  const fetchAnnouncement = async () => {
    try {
      setLoading(true);
      const response = await announcementService.getAnnouncementById(id);
      setAnnouncement(response.data.announcement);
    } catch (err) {
      setError('Failed to load announcement');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const renderAttachment = () => {
    if (!announcement) return null;

    const fileUrl = `${config.UPLOAD_BASE_URL}/${announcement.file_path}`;
    const fileType = announcement.file_type;

    if (fileType.startsWith('image/')) {
      return (
        <div className="text-center">
          <img
            src={fileUrl}
            alt={announcement.title}
            className="img-fluid rounded shadow"
            style={{ maxHeight: '70vh' }}
          />
        </div>
      );
    } else if (fileType === 'application/pdf') {
      return (
        <div className="text-center">
          <iframe
            src={fileUrl}
            width="100%"
            height="600px"
            style={{ border: '1px solid #ddd', borderRadius: '5px' }}
            title={announcement.title}
          >
            <p>Your browser does not support iframes.
              <a href={fileUrl} target="_blank" rel="noopener noreferrer">Click here to view the PDF</a>
            </p>
          </iframe>
        </div>
      );
    } else {
      return (
        <div className="text-center">
          <div className="file-preview">
            <i className="fas fa-file-alt fa-4x text-primary mb-3"></i>
            <p className="mb-3">This file type cannot be previewed directly.</p>
            <Button
              variant="primary"
              size="lg"
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="fas fa-download me-2"></i>
              Download File
            </Button>
          </div>
        </div>
      );
    }
  };

  if (loading) {
    return (
      <Container className="py-5">
        <div className="text-center">
          <Spinner animation="border" size="lg" />
          <p className="mt-3">Loading announcement...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger" className="text-center">
          <h4>Oops!</h4>
          <p>{error}</p>
          <Button variant="primary" onClick={() => navigate('/')}>
            Go Back Home
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Row className="justify-content-center">
        <Col lg={10} xl={8}>
          <Card className="shadow">
            <Card.Header className="bg-light">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h4 className="mb-1">Controller of Examinations</h4>
                  <small className="text-muted">
                    Announcement Date: {formatDate(announcement.announcement_date)}
                  </small>
                </div>
                <Button variant="outline-secondary" onClick={() => navigate('/')}>
                  <i className="fas fa-arrow-left me-2"></i>
                  Back to Home
                </Button>
              </div>
            </Card.Header>

            <Card.Body>
              <div className="announcement-header mb-4">
                <h3 className="text-primary mb-3">{announcement.title}</h3>
                <hr />
              </div>

              <div className="announcement-content">
                {renderAttachment()}
              </div>
            </Card.Body>

            <Card.Footer className="bg-light text-muted">
              <small>
                <i className="fas fa-info-circle me-1"></i>
                If the file doesn't load properly, try refreshing the page or use a different browser.
              </small>
            </Card.Footer>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default AnnouncementView;